import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, ChevronLeft } from "lucide-react";
import logo from "./AuthLogo.png";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export default function LoginPage() {
  const [, navigate] = useLocation();
  const { user, requestMagicLinkMutation } = useAuth();
  const [emailSent, setEmailSent] = useState(false);

  const loginForm = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
    },
  });

  const onLoginSubmit = (values) => {
    requestMagicLinkMutation.mutate(values.email, {
      onSuccess: () => {
        setEmailSent(true);
        // Reset form
        loginForm.reset();
      },
    });
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log("User already logged in, checking first login status...");
      window.scrollTo(0, 0);
      // First-time users should see the welcome page
      if (user.isConsent) {
        navigate("/onboarding");
      } else {
        navigate("/welcome");
      }
    }
  }, [user, navigate]);

  // Show success message if email was sent
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#eaf1f3]">
        <button
          onClick={() => navigate("/landing-page")}
          className="absolute top-4 left-4 flex items-center text-[#4c7375] font-semibold hover:underline"
        >
          <ChevronLeft className="h-5 w-5 mr-2" />
          <span className="text-lg text-[#4c7375] font-medium">Back</span>
        </button>
        <div className="w-full max-w-md px-5 my-3">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img
                src={logo}
                alt="In My Name KYC Logo"
                className="h-[6rem] w-auto"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#243d3d] mb-2">
              Check your email!
            </h2>
            <p className="text-[#4c7375] mb-6">
              We've sent you a magic link to sign in. Click the link in your
              email to access your account.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => setEmailSent(false)}
                variant="outline"
                className="w-full border-[#4c7375] text-[#4c7375] hover:bg-[#4c7375] hover:text-white rounded-[6px] h-12"
              >
                Send another link
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#eaf1f3]">
      <button
        onClick={() => navigate("/landing-page")}
        className="absolute top-4 left-4 flex items-center text-[#4c7375] font-semibold hover:underline"
      >
        <ChevronLeft className="h-5 w-5 mr-2" />
        <span className="text-lg text-[#4c7375] font-medium">Back</span>
      </button>
      <div className="w-full max-w-md px-5 my-3">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-10">
            <img
              src={logo}
              alt="In My Name KYC Logo"
              className="h-[6rem] w-auto"
            />
          </div>
          <h1 className="text-2xl font-bold text-[#4c7375] mb-2">
            Welcome back
          </h1>
          <p className="text-[#4c7375]">
            Enter your email to receive a secure sign-in link
          </p>
        </div>

        <Form {...loginForm}>
          <div className="space-y-6">
            <FormField
              control={loginForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#76ADAA] font-semibold">
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Email"
                      autoComplete="username"
                      {...field}
                      className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full bg-[#4c7375] hover:bg-teal-700 rounded-[6px] h-12 text-white font-semibold shadow-lg"
              disabled={requestMagicLinkMutation.isPending}
              onClick={loginForm.handleSubmit(onLoginSubmit)}
            >
              {requestMagicLinkMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending magic link...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-5 w-5" />
                  Send magic link
                </>
              )}
            </Button>
          </div>
        </Form>

        <div className="mt-2">
          <div className="text-center">
            <span className="text-[#4c7375]">
              Don't have an active account?{" "}
            </span>
            <button
              className="text-[#4c7375] hover:text-[#4c7375] font-semibold underline"
              onClick={() => navigate("/account-activation")}
            >
              Activate now
            </button>
          </div>
        </div>

        {/* Info about magic links */}
        <div className="mt-6 p-4 bg-[#d7efef] rounded-lg">
          <p className="text-xs text-[#4c7375] text-center">
            We'll send you a secure link that expires in 15 minutes. No password
            required - just click the link to sign in.
          </p>
        </div>
      </div>
    </div>
  );
}
